import React, { useEffect, useState } from 'react';
import ExcelJS from 'exceljs';
import { Download } from 'lucide-react';
import { useApiData } from '../../contexts/apiService';
import IconTooltip from '../../components/common/icon_tooltip/iconTooltip';

interface Contributor {
    lease: string;
    producer: string | null;
    barrels: number;
    deliveries: number;
    pct: number;
}

interface MarketTarget {
    marketId: number;
    label: string;
}

interface MarketContributorsModalProps {
    market: MarketTarget;
    anchorDate?: string | null;
    onClose: () => void;
}

// How many days before the sample to default the delivery window to.
const ANCHOR_WINDOW_DAYS = 30;

const fmtBbl = (n: number) =>
    n.toLocaleString(undefined, { maximumFractionDigits: 2 });

const pad2 = (n: number) => String(n).padStart(2, '0');

// Local YYYY-MM-DD (presets anchor on the user's "today", no timezone shift needed).
const ymd = (d: Date): string =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;


const anchorRange = (anchor?: string | null): { from: string; to: string } => {
    if (!anchor) return { from: '', to: '' };
    const d = new Date(`${anchor}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ANCHOR_WINDOW_DAYS);
    const from = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
    return { from, to: anchor };
};

const sanitize = (s: string): string =>
    s.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'market';

type Preset = 'thisMonth' | 'lastMonth' | '90d' | 'ytd' | 'all';

const MarketContributorsModal: React.FC<MarketContributorsModalProps> = ({ market, anchorDate, onClose }) => {
    const { handleGetMarketContributors } = useApiData();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [contributors, setContributors] = useState<Contributor[]>([]);
    const [totalBarrels, setTotalBarrels] = useState<number>(0);

    // The modal owns its delivery-date range. Defaults to the 30-day pre-sample
    // window when opened from a bar (anchorDate), else All (empty = no filter).
    const initial = anchorRange(anchorDate);
    const [draftFrom, setDraftFrom] = useState<string>(initial.from);
    const [draftTo, setDraftTo] = useState<string>(initial.to);
    const [appliedFrom, setAppliedFrom] = useState<string>(initial.from);
    const [appliedTo, setAppliedTo] = useState<string>(initial.to);
    const [activePreset, setActivePreset] = useState<string>(anchorDate ? '' : 'all');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        handleGetMarketContributors(market.marketId, appliedFrom || null, appliedTo || null)
            .then((res: any) => {
                if (cancelled) return;
                if (!res || res.success === false) {
                    setError(res?.error || 'Could not load contributing leases.');
                    setContributors([]);
                    setTotalBarrels(0);
                    return;
                }
                // Drop zero-contribution rows — leases with no barrels in the range
                const rows: Contributor[] = Array.isArray(res.contributors) ? res.contributors : [];
                setContributors(rows.filter((c) => Number(c.barrels) > 0));
                setTotalBarrels(Number(res.total_barrels) || 0);
            })
            .catch((e: any) => {
                if (!cancelled) setError(e?.message || 'Could not load contributing leases.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };

    }, [market.marketId, appliedFrom, appliedTo]);

    const applyDraftRange = () => {
        setAppliedFrom(draftFrom);
        setAppliedTo(draftTo);
        setActivePreset('');
    };
    const rangeDirty = draftFrom !== appliedFrom || draftTo !== appliedTo;

    const applyPreset = (preset: Preset) => {
        const now = new Date();
        let f = '';
        let t = '';
        if (preset === 'thisMonth') {
            f = ymd(new Date(now.getFullYear(), now.getMonth(), 1));
            t = ymd(now);
        } else if (preset === 'lastMonth') {
            f = ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1));
            t = ymd(new Date(now.getFullYear(), now.getMonth(), 0)); // day 0 = last day of prev month
        } else if (preset === '90d') {
            const d = new Date(now);
            d.setDate(d.getDate() - 90);
            f = ymd(d);
            t = ymd(now);
        } else if (preset === 'ytd') {
            f = ymd(new Date(now.getFullYear(), 0, 1));
            t = ymd(now);
        }
        // 'all' leaves both empty.
        setDraftFrom(f);
        setDraftTo(t);
        setAppliedFrom(f);
        setAppliedTo(t);
        setActivePreset(preset);
    };

    const rangeLabel = appliedFrom || appliedTo
        ? `${appliedFrom || '…'} → ${appliedTo || '…'}`
        : 'All dates';

    const canDownload = !loading && !error && contributors.length > 0;


    const handleDownload = async () => {
        if (!canDownload) return;
        try {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet('Contributing Leases');
            ws.columns = [
                { key: 'rank', width: 6 },
                { key: 'lease', width: 32 },
                { key: 'producer', width: 26 },
                { key: 'barrels', width: 16 },
                { key: 'deliveries', width: 12 },
                { key: 'pct', width: 12 },
            ];

            // Descriptive header so the file explains itself when emailed.
            const titleRow = ws.addRow([`Market: ${market.label}`]);
            titleRow.font = { bold: true, size: 13 };
            ws.addRow([`Date range: ${rangeLabel}`]);
            ws.addRow([`Total barrels: ${fmtBbl(totalBarrels)}`]);
            ws.addRow([`Generated: ${new Date().toLocaleString()}`]);
            ws.addRow([]); // spacer

            const headerRow = ws.addRow(['#', 'Lease', 'Producer', 'Barrels', 'Deliveries', 'Share %']);
            headerRow.font = { bold: true };
            headerRow.eachCell((cell) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2F7' } };
            });

            contributors.forEach((c, i) => {
                const row = ws.addRow([
                    i + 1,
                    c.lease,
                    c.producer || '',
                    Number(c.barrels),
                    Number(c.deliveries),
                    Number(c.pct),
                ]);
                row.getCell(4).numFmt = '#,##0.00';
                row.getCell(5).numFmt = '0';
                row.getCell(6).numFmt = '0.0"%"';
            });

            const fnFrom = appliedFrom || 'all';
            const fnTo = appliedTo || 'all';
            const filename = `leases_${sanitize(market.label)}_${fnFrom}_${fnTo}.xlsx`;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Error exporting contributing leases:', e);
        }
    };

    return (
        <div className="labContrib-overlay" onClick={onClose}>
            <div className="labContrib-modal" onClick={(e) => e.stopPropagation()}>
                <div className="labContrib-header">
                    <div className="labContrib-header-row">
                        <div>
                            <h3 className="labContrib-title">
                                Leases contributing to {market.label}
                            </h3>
                            <div className="labContrib-subtitle">
                                {rangeLabel}
                                {!loading && !error && (
                                    <> · {contributors.length} lease{contributors.length === 1 ? '' : 's'} · {fmtBbl(totalBarrels)} bbl total</>
                                )}
                            </div>
                        </div>
                        <div className="labContrib-header-actions">
                            <IconTooltip
                                icon={Download}
                                onClick={handleDownload}
                                disabled={!canDownload}
                                showTooltip={true}
                                label="Download Table"
                                position="bottom"
                                iconColor="var(--color-green-download)"
                            />
                            <button onClick={onClose} className="labContrib-close" title="Close">
                                ×
                            </button>
                        </div>
                    </div>

                    {/* Delivery-date range controls */}
                    <div className="labContrib-controls">
                        <div className="labContrib-controls-row">
                            <span className="labContrib-controls-label">Delivery date</span>
                            <div className="labContrib-date-group">
                                <input
                                    type="date"
                                    lang="en-US"
                                    className="labContrib-date"
                                    value={draftFrom}
                                    onChange={(e) => { setDraftFrom(e.target.value); setActivePreset(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') applyDraftRange(); }}
                                />
                                <span className="labContrib-date-sep">→</span>
                                <input
                                    type="date"
                                    lang="en-US"
                                    className="labContrib-date"
                                    value={draftTo}
                                    onChange={(e) => { setDraftTo(e.target.value); setActivePreset(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') applyDraftRange(); }}
                                />
                            </div>
                            <button
                                type="button"
                                className={`labContrib-apply-btn ${rangeDirty ? 'is-dirty' : ''}`}
                                onClick={applyDraftRange}
                                disabled={!rangeDirty}
                                title="Apply the selected date range"
                            >
                                Apply
                            </button>
                        </div>
                        <div className="labContrib-presets" role="group" aria-label="Date presets">
                            <button type="button" className={`labContrib-preset-btn ${activePreset === 'thisMonth' ? 'is-active' : ''}`} onClick={() => applyPreset('thisMonth')}>This month</button>
                            <button type="button" className={`labContrib-preset-btn ${activePreset === 'lastMonth' ? 'is-active' : ''}`} onClick={() => applyPreset('lastMonth')}>Last month</button>
                            <button type="button" className={`labContrib-preset-btn ${activePreset === '90d' ? 'is-active' : ''}`} onClick={() => applyPreset('90d')}>90d</button>
                            <button type="button" className={`labContrib-preset-btn ${activePreset === 'ytd' ? 'is-active' : ''}`} onClick={() => applyPreset('ytd')}>YTD</button>
                            <button type="button" className={`labContrib-preset-btn ${activePreset === 'all' ? 'is-active' : ''}`} onClick={() => applyPreset('all')}>All</button>
                        </div>
                        {anchorDate && (
                            <span className="labContrib-controls-hint">
                                Showing the {ANCHOR_WINDOW_DAYS} days of deliveries before the sample ({anchorDate}) — the crude that composed it. Use the presets for other ranges.
                            </span>
                        )}
                    </div>
                </div>

                <div className="labContrib-body">
                    {loading && (
                        <div className="labContrib-state">
                            Loading contributing leases…
                        </div>
                    )}

                    {!loading && error && (
                        <div className="labContrib-error">
                            {error}
                        </div>
                    )}

                    {!loading && !error && contributors.length === 0 && (
                        <div className="labContrib-state">
                            No deliveries found for this market in the selected range.
                        </div>
                    )}

                    {!loading && !error && contributors.length > 0 && (
                        <table className="labContrib-table">
                            <thead>
                                <tr>
                                    <th className="labContrib-th-rank">#</th>
                                    <th>Lease</th>
                                    <th>Producer</th>
                                    <th className="labContrib-th-barrels">Barrels</th>
                                    <th className="labContrib-th-share">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contributors.map((c, i) => (
                                    <tr key={`${c.lease}|${c.producer}|${i}`}>
                                        <td className="labContrib-td-rank">{i + 1}</td>
                                        <td className="labContrib-td-lease">{c.lease}</td>
                                        <td className="labContrib-td-producer">{c.producer || '—'}</td>
                                        <td className="labContrib-td-barrels">{fmtBbl(c.barrels)}</td>
                                        <td>
                                            <div className="labContrib-share-cell">
                                                <div className="labContrib-share-bar">
                                                    <div className="labContrib-share-fill" style={{ width: `${Math.min(100, c.pct)}%` }} />
                                                </div>
                                                <span className="labContrib-share-pct">{c.pct}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarketContributorsModal;
